import { ContactOverviewModel } from "src/types/contact/ContactOverviewModel"
import { UnreadReplyCountCacheService } from "./UnreadReplyCountCacheService"
import { storageHelpers, CACHE_KEY_PREFIX } from "src/storage/mmkv"

/**
 * 楽観的状態管理サービス
 *
 * フィルター切り替え時にも楽観的更新を維持するため、
 * 以下の状態を一元管理する:
 * - 楽観的に既読マークした連絡ID
 * - 楽観的にお気に入りにした連絡データ
 *
 * 注意: このサービスはシングルトンとして動作し、
 * アプリのライフサイクル中は状態を維持する。
 * Pull-to-Refreshでサーバーの最新状態を反映する際にクリアする。
 *
 * 楽観的既読状態はMMKVに永続化されるため、
 * 団体切り替えやアプリ再起動後も維持される。
 */

// MMKVに保存する楽観的既読データの型
interface OptimisticReadCache {
  contactIds: string[]
  order: string[]
  savedAt: number
}

// MMKVキー
const OPTIMISTIC_READ_CACHE_KEY = `${CACHE_KEY_PREFIX.CONTACT_LIST}_optimistic_read`
// キャッシュの有効期限（24時間）- サーバー側で既読になるまでの猶予
const OPTIMISTIC_READ_TTL = 24 * 60 * 60 * 1000

class OptimisticStateServiceClass {
  // 楽観的に既読マークした連絡ID（メモリSet）
  private optimisticallyReadContactIds: Set<string> = new Set()
  // LRUキャッシュ用の追加順序
  private optimisticallyReadOrder: string[] = []
  // 最大保持件数（アプリの最大連絡数と同じ）
  private readonly MAX_OPTIMISTIC_READ_ITEMS = 5000
  // 初期化済みフラグ
  private initialized = false

  // 楽観的にお気に入りにした連絡（キー: contactId, 値: 連絡データ）
  private optimisticallyFavoritedContacts: Map<string, ContactOverviewModel> = new Map()

  // 状態変更時のコールバック（UIの再レンダリング用）
  private updateCallbacks: Array<() => void> = []

  /**
   * 状態変更時のコールバックを登録
   */
  addUpdateCallback(callback: () => void): void {
    this.updateCallbacks.push(callback)
  }

  /**
   * コールバックを削除
   */
  removeUpdateCallback(callback: () => void): void {
    this.updateCallbacks = this.updateCallbacks.filter((cb) => cb !== callback)
  }

  /**
   * コールバックを実行
   */
  private notifyUpdate(): void {
    this.updateCallbacks.forEach((callback) => callback())
  }

  // ========================================
  // MMKV永続化
  // ========================================

  /**
   * MMKVから楽観的既読状態を読み込み（初回のみ）
   */
  private ensureInitialized(): void {
    if (this.initialized) return

    try {
      const cached = storageHelpers.getObject<OptimisticReadCache>(OPTIMISTIC_READ_CACHE_KEY)
      if (cached && cached.savedAt) {
        const now = Date.now()
        // TTL内であれば復元
        if (now - cached.savedAt < OPTIMISTIC_READ_TTL) {
          this.optimisticallyReadContactIds = new Set(cached.contactIds || [])
          this.optimisticallyReadOrder = cached.order || []
          console.log("[OptimisticStateService] MMKVから楽観的既読を復元:", {
            count: this.optimisticallyReadContactIds.size,
            ageMinutes: Math.round((now - cached.savedAt) / 1000 / 60),
          })
        } else {
          // TTL切れの場合は削除
          storageHelpers.delete(OPTIMISTIC_READ_CACHE_KEY)
          console.log("[OptimisticStateService] MMKVの楽観的既読がTTL切れのため削除")
        }
      }
    } catch (error) {
      console.error("[OptimisticStateService] MMKVからの読み込みに失敗:", error)
    }

    this.initialized = true
  }

  /**
   * 楽観的既読状態をMMKVに保存
   */
  private saveToMMKV(): void {
    try {
      const cache: OptimisticReadCache = {
        contactIds: Array.from(this.optimisticallyReadContactIds),
        order: this.optimisticallyReadOrder,
        savedAt: Date.now(),
      }
      storageHelpers.setObject(OPTIMISTIC_READ_CACHE_KEY, cache)
    } catch (error) {
      console.error("[OptimisticStateService] MMKVへの保存に失敗:", error)
    }
  }

  // ========================================
  // 楽観的既読の管理
  // ========================================

  /**
   * 連絡が楽観的に既読マークされているか確認
   * メモリとキャッシュの両方をチェック
   */
  isOptimisticallyRead(contactId: string): boolean {
    // 初回はMMKVから復元
    this.ensureInitialized()

    // メモリ上のSetをチェック
    if (this.optimisticallyReadContactIds.has(contactId)) {
      return true
    }

    // UnreadReplyCountCacheServiceのキャッシュもチェック（画面遷移後も正しく動作するため）
    const cachedData = UnreadReplyCountCacheService.getCache(contactId)
    if (cachedData && cachedData.isOptimisticallyRead) {
      // キャッシュにあればメモリにも追加（次回以降のチェック用）
      this.addToOptimisticReadCache(contactId)
      return true
    }

    return false
  }

  /**
   * 連絡を楽観的に既読マーク
   */
  markAsRead(contactId: string): void {
    // 初回はMMKVから復元
    this.ensureInitialized()

    this.addToOptimisticReadCache(contactId)
    // MMKVに保存（団体切り替え後も維持するため）
    this.saveToMMKV()
    console.log("[OptimisticStateService] 楽観的既読に追加:", {
      contactId,
      total: this.optimisticallyReadContactIds.size,
    })
  }

  /**
   * LRUキャッシュに追加（上限管理あり）
   */
  private addToOptimisticReadCache(contactId: string): void {
    if (!this.optimisticallyReadContactIds.has(contactId)) {
      // 既に上限に達している場合は最も古いアイテムを削除
      if (this.optimisticallyReadContactIds.size >= this.MAX_OPTIMISTIC_READ_ITEMS) {
        const oldestId = this.optimisticallyReadOrder.shift()
        if (oldestId) {
          this.optimisticallyReadContactIds.delete(oldestId)
          console.log(`[OptimisticStateService] LRU: 古い既読アイテムを削除: ${oldestId}`)
        }
      }
      this.optimisticallyReadContactIds.add(contactId)
      this.optimisticallyReadOrder.push(contactId)
    }
  }

  /**
   * キャッシュから楽観的既読を復元（連絡一覧取得時に呼び出す）
   */
  restoreOptimisticallyReadFromCache(contactIds: string[]): number {
    // 初回はMMKVから復元
    this.ensureInitialized()

    let restoredCount = 0
    contactIds.forEach((contactId) => {
      if (!this.optimisticallyReadContactIds.has(contactId)) {
        const cached = UnreadReplyCountCacheService.getCache(contactId)
        if (cached && cached.isOptimisticallyRead) {
          this.addToOptimisticReadCache(contactId)
          restoredCount++
        }
      }
    })

    if (restoredCount > 0) {
      console.log("[OptimisticStateService] キャッシュから楽観的既読を復元", {
        restoredCount,
        totalOptimisticallyRead: this.optimisticallyReadContactIds.size,
      })
      // 復元した場合はMMKVにも保存
      this.saveToMMKV()
    }

    return restoredCount
  }

  // ========================================
  // 楽観的お気に入りの管理
  // ========================================

  /**
   * 連絡が楽観的にお気に入りマークされているか確認
   */
  isOptimisticallyFavorited(contactId: string): boolean {
    return this.optimisticallyFavoritedContacts.has(contactId)
  }

  /**
   * 楽観的にお気に入りにした連絡データを取得
   */
  getOptimisticallyFavoritedContact(contactId: string): ContactOverviewModel | undefined {
    return this.optimisticallyFavoritedContacts.get(contactId)
  }

  /**
   * すべての楽観的にお気に入りにした連絡を取得
   */
  getAllOptimisticallyFavoritedContacts(): ContactOverviewModel[] {
    return Array.from(this.optimisticallyFavoritedContacts.values())
  }

  /**
   * 連絡を楽観的にお気に入りマーク
   */
  setFavorited(contactId: string, contact: ContactOverviewModel): void {
    this.optimisticallyFavoritedContacts.set(contactId, { ...contact, isFavorite: true })
    console.log("[OptimisticStateService] 楽観的お気に入りに追加:", { contactId })
    this.notifyUpdate()
  }

  /**
   * 楽観的お気に入りを解除
   */
  removeFavorited(contactId: string): void {
    if (this.optimisticallyFavoritedContacts.has(contactId)) {
      this.optimisticallyFavoritedContacts.delete(contactId)
      console.log("[OptimisticStateService] 楽観的お気に入りから削除:", { contactId })
      this.notifyUpdate()
    }
  }

  // ========================================
  // 状態のクリア
  // ========================================

  /**
   * 楽観的既読をクリア（メモリのみ）
   *
   * 団体切り替え時に使用。MMKVは保持するため、
   * 同じ団体に戻った際に楽観的既読が復元される。
   */
  clearOptimisticallyRead(): void {
    this.optimisticallyReadContactIds.clear()
    this.optimisticallyReadOrder = []
    // initializedをリセットして、次回アクセス時にMMKVから再読み込みする
    this.initialized = false
    console.log("[OptimisticStateService] 楽観的既読をクリア（メモリのみ）")
  }

  /**
   * 楽観的既読を完全にクリア（メモリ + MMKV）
   *
   * Pull-to-Refresh時に使用。サーバーから最新の未読状態を取得するため、
   * MMKVの楽観的既読もクリアする。
   */
  clearOptimisticallyReadCompletely(): void {
    this.optimisticallyReadContactIds.clear()
    this.optimisticallyReadOrder = []
    // MMKVもクリア
    storageHelpers.delete(OPTIMISTIC_READ_CACHE_KEY)
    console.log("[OptimisticStateService] 楽観的既読を完全にクリア（メモリ + MMKV）")
  }

  /**
   * 楽観的お気に入りをクリア
   */
  clearOptimisticallyFavorited(): void {
    this.optimisticallyFavoritedContacts.clear()
    console.log("[OptimisticStateService] 楽観的お気に入りをクリア")
    this.notifyUpdate()
  }

  /**
   * すべての楽観的状態をクリア（団体切り替え時に使用）
   *
   * メモリのみクリアし、MMKVは保持する。
   * これにより、同じ団体に戻った際に楽観的既読が復元される。
   */
  clearAll(): void {
    this.clearOptimisticallyRead()
    this.clearOptimisticallyFavorited()
    console.log("[OptimisticStateService] すべての楽観的状態をクリア（メモリのみ）")
  }

  /**
   * すべての楽観的状態を完全にクリア（Pull-to-Refresh時に使用）
   *
   * メモリとMMKVの両方をクリアする。
   * サーバーから最新の未読状態を取得する際に使用。
   */
  clearAllCompletely(): void {
    this.clearOptimisticallyReadCompletely()
    this.clearOptimisticallyFavorited()
    console.log("[OptimisticStateService] すべての楽観的状態を完全にクリア（メモリ + MMKV）")
  }

  // ========================================
  // デバッグ用
  // ========================================

  /**
   * 現在の状態を取得（デバッグ用）
   */
  getDebugInfo(): {
    optimisticallyReadCount: number
    optimisticallyFavoritedCount: number
    optimisticallyReadIds: string[]
    optimisticallyFavoritedIds: string[]
  } {
    // 初回はMMKVから復元
    this.ensureInitialized()

    return {
      optimisticallyReadCount: this.optimisticallyReadContactIds.size,
      optimisticallyFavoritedCount: this.optimisticallyFavoritedContacts.size,
      optimisticallyReadIds: Array.from(this.optimisticallyReadContactIds),
      optimisticallyFavoritedIds: Array.from(this.optimisticallyFavoritedContacts.keys()),
    }
  }
}

// シングルトンインスタンスをエクスポート
export const OptimisticStateService = new OptimisticStateServiceClass()
